import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { classNames } from "../../helpers";
import { FcDrawer } from "../../modals/classes/schedule/ui";
import * as userActions from "../../redux/reducers/userReducer";
import { userState } from "../../redux/reducers/userReducer";
import axiosInstance from "../../utils/axiosInstance";
import { refreshUserDataOnce } from "../PaymentsRequired";
import { getUrlForBookings, getUrlForUsers } from "../../utils/confighelpers";

/**
 * Setup guide (club owners, businessType "class"): header button with a progress ring + a drawer with the
 * setup steps in order. Each step is ticked from data the club already has; nothing is stored.
 *   profile  - logo, about text (5+ chars) and address on myBusinessProfile
 *   stripe   - myConnectedPaymentProfiles.stripe onboarding status SUCCESS
 *   class    - at least one class (listOfClasses)
 *   products - at least one product (products/list, the automatic "free trial credit" product not counted)
 *   schedule - at least one session in the next 30 days (getMyClubSchedule)
 *   members  - at least one member (getMyClubMembers total)
 *   widgets  - OPTIONAL (2026-11-05) "Embed on your website": Settings › Widgets. Not counted in "N of 6" / the ring / "ready";
 *              ticked when the club saved a widget look (users.webWidgets.look) or copied / emailed the code in this browser
 *              (localStorage fc.widgetsShared.<userId>, set by WebsiteWidgetsPage via markWidgetsShared).
 * The four list checks come from one light request (bookings/setupStatus: "is there at least one?" per step).
 * Faster and steadier (2026-10-28):
 *   - the result is kept per club in this browser (5 minutes), so page changes don't ask again;
 *   - in the header a list step that was done stays done, and a failed request keeps the last known answer
 *     (it used to show "missing" whenever a list was slow);
 *   - profile and payments are read from the club's own data, never kept from before: when either looks missing the
 *     data is reloaded once, so the guide and the "Set up payments" banner always agree;
 *   - once all six steps are done, page loads don't check at all. Opening the guide always re-checks.
 */

const CACHE_MS = 5 * 60 * 1000;
const LIST_KEYS = ["class", "products", "schedule", "members"];
const storeKey = (userId) => `fc.setupGuide.${userId}`;
const readStore = (userId) => {
  try {
    const v = JSON.parse(window.localStorage.getItem(storeKey(userId)) || "null");
    return v && typeof v === "object" && v.done ? v : null;
  } catch (e) {
    return null;
  }
};
const writeStore = (userId, v) => {
  try {
    window.localStorage.setItem(storeKey(userId), JSON.stringify(v));
  } catch (e) {
    /* private mode: the guide still works, it just asks again next time */
  }
};
let memory = { userId: "", value: null }; // when localStorage isn't available

const sharedKey = (userId) => `fc.widgetsShared.${userId}`;
/** Settings › Widgets: the club copied or emailed its widget code (optional setup step). */
export const markWidgetsShared = (userId) => {
  if (!userId) return;
  try {
    window.localStorage.setItem(sharedKey(userId), "1");
  } catch (e) {
    /* private mode: the step just stays open */
  }
};
const widgetsShared = (userId) => {
  try {
    return !!userId && window.localStorage.getItem(sharedKey(userId)) === "1";
  } catch (e) {
    return false;
  }
};

/** -> { class, products, schedule, members } booleans, or null when the request failed */
async function loadStatus() {
  try {
    const res = await axiosInstance({ method: "get", url: `${getUrlForBookings()}setupStatus` });
    const d = res?.status === 200 && res?.data?.statusCode === 200 ? res.data.data : null;
    if (!d) return null;
    return { class: d.classes > 0, products: d.products > 0, schedule: d.sessions > 0, members: d.members > 0 };
  } catch (e) {
    return null;
  }
}

const hasProfile = (userData) => {
  const p = userData?.myBusinessProfile || {};
  const address = p.address && (p.address.address || p.address.description);
  return !!(p.logo && String(p.about || "").trim().length >= 5 && address);
};
const hasStripe = (userData) => {
  const d = userData?.myConnectedPaymentProfiles?.stripe?.profileDetails || {};
  return !!d.stripeAcId && d.stripeOnBoardindStatus === "SUCCESS";
};

const STEPS = [
  {
    key: "profile",
    title: "Complete your club profile",
    sub: "Logo, about text and address",
    body: "Members see your logo, description and address in the FitClub app and on your club page.",
    cta: "Open club profile",
    to: "/my-account/company",
  },
  {
    key: "stripe",
    title: "Connect Stripe",
    sub: "So members can pay online",
    body: "Connect your Stripe account to sell passes and subscriptions. Money goes straight to your own account.",
    cta: "Connect Stripe",
    to: "/my-account/online-payment",
  },
  {
    key: "class",
    title: "Add your first class",
    sub: "What you offer, e.g. Sunrise Yoga",
    body: "Add each type of class you run, with a photo, description, duration and capacity.",
    cta: "Go to My Classes",
    to: "/myclasses",
  },
  { key: "products", title: "Create your products", sub: "Passes and subscriptions members buy", cta: "Go to My Products", to: "/products" },
  {
    key: "schedule",
    title: "Put classes on your schedule",
    sub: "When and where each class runs",
    body: "Add sessions to your timetable: once, weekly or on set dates. Members can book as soon as they are on.",
    cta: "Go to My Schedule",
    to: "/myclasses/myschedule",
  },
  { key: "members", title: "Add your members", sub: "Invite new members or move existing ones", to: "/mymembers" },
];

/** Optional steps: shown after the steps above, never counted in the progress (2026-11-05). */
const OPTIONAL_STEPS = [
  {
    key: "widgets",
    title: "Embed on your website",
    sub: "Show your classes and shop on your own site",
    body: "Add Try a class, Join the club and Buy credits to your own website. Pick the colours, copy the code and paste it into your site (or email it to your web designer).",
    cta: "Open Widgets",
    to: "/my-account/widgets",
  },
];

const Check = ({ className = "h-3.5 w-3.5" }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const Btn = ({ children, onClick, secondary = false }) => (
  <button
    type="button"
    onClick={onClick}
    className={classNames(
      "inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-xl px-4 text-sm font-extrabold",
      secondary ? "border border-[#E3DCE6] bg-white text-gray-900 hover:bg-[#FAF8FB]" : "bg-brown-coffee text-white hover:bg-light-brown-coffee"
    )}
  >
    {children}
  </button>
);

const Num = ({ n }) => (
  <span className="mt-px flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-[#F3E9FF] text-xs font-black text-[#5a00a0]">{n}</span>
);

export default function SetupGuide() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userReduxState = useSelector(userState);
  const userData = userReduxState.userData;
  const businessType = userReduxState.businessType;
  const userId = userData?._id || "";
  const [saved, setSaved] = React.useState(() => (userId ? readStore(userId) || (memory.userId === userId ? memory.value : null) : null));
  const [open, setOpen] = React.useState(false);
  const [openStep, setOpenStep] = React.useState(null);
  const [path, setPath] = React.useState("fresh");

  // Only club owners (businessType "class") see the guide; members and other business types load nothing.
  const enabled = !!userId && userData?.userType !== "student" && businessType === "class";

  const save = React.useCallback(
    (value) => {
      memory = { userId, value };
      writeStore(userId, value);
      setSaved(value);
    },
    [userId]
  );

  /**
   * fresh = true (guide opened): show exactly what the club has now.
   * fresh = false (page load): a step that was done stays done; nothing is asked when everything is done
   * or when the last answer is less than 5 minutes old.
   */
  const refresh = React.useCallback(
    async (fresh = false) => {
      if (!enabled) return;
      const prev = readStore(userId) || (memory.userId === userId ? memory.value : null);
      if (!fresh && prev && (prev.allDone || Date.now() - (prev.at || 0) < CACHE_MS)) {
        setSaved(prev);
        return;
      }
      const status = await loadStatus();
      if (!status) {
        if (prev) setSaved(prev); // keep the last known answer
        return;
      }
      const latest = readStore(userId) || (memory.userId === userId ? memory.value : null) || prev; // may have changed meanwhile
      const done = { ...(latest?.done || {}) };
      ["class", "products", "schedule", "members"].forEach((k) => {
        done[k] = fresh ? status[k] : status[k] || !!done[k];
      });
      ["profile", "stripe"].forEach((k) => delete done[k]); // not kept (see above)
      save({ done, at: Date.now(), allDone: LIST_KEYS.every((k) => done[k]), checked: true });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId, enabled, save]
  );

  React.useEffect(() => {
    if (userId) setSaved(readStore(userId) || (memory.userId === userId ? memory.value : null));
    refresh(false);
  }, [refresh, userId]);

  // profile + payments come from the club's own data (Redux); when either looks missing, reload it once from the server
  const liveProfile = hasProfile(userData);
  const liveStripe = hasStripe(userData);
  React.useEffect(() => {
    if (enabled && (!liveProfile || !liveStripe)) refreshUserDataOnce(dispatch, userData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, liveProfile, liveStripe]);

  const savedDone = saved?.done || {};
  const done = {
    profile: liveProfile,
    stripe: liveStripe,
    class: !!savedDone.class,
    products: !!savedDone.products,
    schedule: !!savedDone.schedule,
    members: !!savedDone.members,
    widgets: !!userData?.webWidgets?.look || widgetsShared(userId),
  };
  const counts = saved?.checked ? saved : null; // "–" in the ring until the first answer
  const doneCount = STEPS.filter((s) => done[s.key]).length;
  const nextKey = (STEPS.find((s) => !done[s.key]) || {}).key || null;
  const pct = Math.round((doneCount / STEPS.length) * 100);
  const allDone = doneCount === STEPS.length;
  const clubName = userData?.myBusinessProfile?.businessName || "your club";

  const openGuide = async () => {
    setOpen(true);
    setOpenStep(nextKey);
    refresh(true);
    try {
      const res = await axiosInstance({ method: "get", url: `${getUrlForUsers()}getUserInfo` });
      if (res?.data?.statusCode === 200) {
        dispatch(userActions.setUserData({ appUrl: userData?.appUrl, ...res.data.data }));
      }
    } catch (e) {
      /* keep what we have */
    }
  };

  const go = (to, state) => {
    setOpen(false);
    navigate(to, state ? { state } : undefined);
  };

  if (!enabled) return null;

  return (
    <>
      <button
        type="button"
        onClick={openGuide}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Setup guide, ${doneCount} of ${STEPS.length} done`}
        className="inline-flex h-10 items-center gap-2.5 rounded-full border border-[#E3DCE6] bg-white p-1 text-sm font-extrabold text-gray-900 hover:bg-[#FAF8FB] sm:pr-3.5"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full" style={{ background: allDone ? "#1F8F4E" : `conic-gradient(#7e00dd ${pct}%, #EDE5F0 0)` }}>
          {allDone ? (
            <Check className="h-4 w-4 text-white" />
          ) : (
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-[10px] font-black text-brown-coffee">{counts ? doneCount : "–"}</span>
          )}
        </span>
        <span className="hidden sm:inline">Setup guide</span>
        {!allDone && <span className="hidden text-xs font-extrabold text-gray-500 md:inline">{doneCount} of {STEPS.length}</span>}
      </button>

      <FcDrawer
        open={open}
        onClose={() => setOpen(false)}
        eyebrow="SETUP GUIDE"
        title={allDone ? `${clubName} is ready` : `Get ${clubName} ready`}
        subtitle={allDone ? "All steps are done. You can come back here any time." : "Follow these steps in order. Each one unlocks the next."}
        hideFooter
      >
        <div className="-mt-1 flex items-center gap-3">
          <span className="h-2 flex-1 overflow-hidden rounded-full bg-[#EDE5F0]">
            <span className="block h-full rounded-full bg-indigo-bz transition-all" style={{ width: `${pct}%` }} />
          </span>
          <span className="text-[13px] font-black text-brown-coffee">{doneCount} of {STEPS.length} done</span>
        </div>

        <ol className="-mt-2 flex flex-col gap-2.5">
          {STEPS.map((s, i) => {
            const isDone = done[s.key];
            const isNext = s.key === nextKey;
            const isOpen = openStep === s.key;
            return (
              <li key={s.key} className={classNames("rounded-[18px] border bg-white", isNext ? "border-[#D9C2F2] shadow-[0_0_0_3px_#F3E9FF]" : "border-[#ECE6EE]")}>
                <button
                  type="button"
                  onClick={() => setOpenStep(isOpen ? null : s.key)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center gap-3 px-4 py-3.5 text-left"
                >
                  <span
                    className={classNames(
                      "flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full text-[13px] font-black",
                      isDone ? "bg-[#1F8F4E] text-white" : isNext ? "border-2 border-indigo-bz bg-white text-indigo-bz" : "border-2 border-[#D7CFDA] bg-white text-[#9a929c]"
                    )}
                  >
                    {isDone ? <Check /> : i + 1}
                    <span className="sr-only">{isDone ? " done" : " to do"}</span>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={classNames("block text-[15px] font-extrabold", isDone ? "text-gray-500" : "text-gray-900")}>{s.title}</span>
                    <span className="mt-px block text-[13px] text-gray-500">{s.sub}</span>
                  </span>
                  {isNext && <span className="rounded-full bg-[#FFE8EB] px-2.5 py-0.5 text-[11px] font-black text-[#B4232F]">Next</span>}
                  <svg viewBox="0 0 24 24" fill="none" stroke="#9a929c" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className={classNames("h-[18px] w-[18px] shrink-0 transition-transform", isOpen && "rotate-180")} aria-hidden="true">
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </button>

                {isOpen && (
                  <div className="flex flex-col gap-3 px-4 pb-4 sm:pl-14">
                    {s.key === "products" ? (
                      <>
                        <p className="text-sm leading-relaxed text-gray-700">Create the passes and subscriptions members can buy, for example a 10-credit pass or a monthly unlimited subscription.</p>
                        <div className="rounded-[14px] bg-[#F3E9FF] px-3.5 py-3 text-[13px] leading-relaxed text-[#3d1466]">
                          <b>Moving from another system?</b> Create the same passes your members have now, with the same name, price and validity (for example "10-session pass" at €85). In step 6 you bring members over onto these passes with the credits they have left. You only need a €0 pass if you want to give free or extra credits.
                        </div>
                        <div className="flex gap-2">
                          <Btn onClick={() => go(s.to)}>{s.cta} →</Btn>
                        </div>
                      </>
                    ) : s.key === "members" ? (
                      <>
                        <div role="tablist" aria-label="How to add members" className="flex gap-1 rounded-xl bg-[#EFE9F2] p-1">
                          {[
                            ["fresh", "New members"],
                            ["migrate", "Moving from another system"],
                          ].map(([k, label]) => (
                            <button
                              key={k}
                              type="button"
                              role="tab"
                              aria-selected={path === k}
                              onClick={() => setPath(k)}
                              className={classNames(
                                "h-9 flex-1 rounded-[9px] px-2 text-[13px] font-extrabold",
                                path === k ? "bg-white text-brown-coffee shadow-[0_1px_3px_rgba(29,19,32,.12)]" : "text-gray-500 hover:text-gray-800"
                              )}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                        {path === "fresh" ? (
                          <>
                            <p className="text-sm leading-relaxed text-gray-700">Share your club link so members sign up themselves, or add them one by one. They get a welcome email to activate their account in the FitClub app.</p>
                            <div className="flex flex-wrap gap-2">
                              <Btn onClick={() => go(s.to, { open: "invite" })}>Invite with club link</Btn>
                              <Btn secondary onClick={() => go(s.to, { open: "add" })}>Add a member</Btn>
                            </div>
                          </>
                        ) : (
                          <>
                            <p className="text-sm leading-relaxed text-gray-700">Bring your current members over with the credits they have left. Do this once per pass type.</p>
                            <div className="flex flex-col gap-2.5">
                              <div className="flex gap-2.5 text-sm leading-relaxed text-gray-700">
                                <Num n={1} />
                                <span>
                                  <b>Before you start:</b> Stripe is connected (step 2) and the same passes exist in My Products (step 4).
                                </span>
                              </div>
                              <div className="flex gap-2.5 text-sm leading-relaxed text-gray-700">
                                <Num n={2} />
                                <span>
                                  <b>Export your members</b> from your old system, one list per pass type: country code, phone number, full name, email and credits left.
                                </span>
                              </div>
                              <div className="flex gap-2.5 text-sm leading-relaxed text-gray-700">
                                <Num n={3} />
                                <span>
                                  <b>Import:</b> in My Members choose <b>Import members</b>, pick the matching pass (for example "10-session pass"), upload the list (or type the rows) and check it.
                                </span>
                              </div>
                              <div className="flex gap-2.5 text-sm leading-relaxed text-gray-700">
                                <Num n={4} />
                                <span>
                                  <b>Done:</b> each member gets an account with their credits on that pass, plus a welcome email to activate it. Nothing is charged.
                                </span>
                              </div>
                            </div>
                            <div className="rounded-[14px] bg-[#FFF8EC] px-3.5 py-3 text-[13px] leading-relaxed text-[#7A4A00]">
                              <b>Good to know:</b> members with 0 credits left can't be imported, so invite them instead. Subscriptions can't be moved, because recurring payments stay with your old system. Import their remaining credits and ask them to start the subscription in the FitClub app. For a single member you can also use <b>Give credits</b> on their member page.
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Btn onClick={() => go(s.to, { open: "import" })}>Import members →</Btn>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="text-sm leading-relaxed text-gray-700">{s.body}</p>
                        <div className="flex gap-2">
                          <Btn onClick={() => go(s.to)}>{s.cta} →</Btn>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ol>

        <div className="mt-1">
          <p className="mb-2 text-[12px] font-black uppercase tracking-wide text-gray-500">Optional</p>
          <ul className="flex flex-col gap-2.5">
            {OPTIONAL_STEPS.map((s) => {
              const isDone = done[s.key];
              const isOpen = openStep === s.key;
              return (
                <li key={s.key} className="rounded-[18px] border border-dashed border-[#D7CFDA] bg-white">
                  <button
                    type="button"
                    onClick={() => setOpenStep(isOpen ? null : s.key)}
                    aria-expanded={isOpen}
                    className="flex w-full items-center gap-3 px-4 py-3.5 text-left"
                  >
                    <span
                      className={classNames(
                        "flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full text-[13px] font-black",
                        isDone ? "bg-[#1F8F4E] text-white" : "border-2 border-dashed border-[#D7CFDA] bg-white text-[#9a929c]"
                      )}
                    >
                      {isDone ? (
                        <Check />
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" className="h-3.5 w-3.5" aria-hidden="true">
                          <path d="M12 5v14M5 12h14" />
                        </svg>
                      )}
                      <span className="sr-only">{isDone ? " done" : " optional"}</span>
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className={classNames("block text-[15px] font-extrabold", isDone ? "text-gray-500" : "text-gray-900")}>{s.title}</span>
                      <span className="mt-px block text-[13px] text-gray-500">{s.sub}</span>
                    </span>
                    <span className="rounded-full bg-[#EFE9F2] px-2.5 py-0.5 text-[11px] font-black text-gray-600">Optional</span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#9a929c" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className={classNames("h-[18px] w-[18px] shrink-0 transition-transform", isOpen && "rotate-180")} aria-hidden="true">
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </button>
                  {isOpen && (
                    <div className="flex flex-col gap-3 px-4 pb-4 sm:pl-14">
                      <p className="text-sm leading-relaxed text-gray-700">{s.body}</p>
                      <div className="flex gap-2">
                        <Btn onClick={() => go(s.to)}>{s.cta} →</Btn>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </FcDrawer>
    </>
  );
}
